import classnames from 'classnames';
import type * as Popper from 'popper.js';
import React, { isValidElement } from 'react';
import { Modal, UncontrolledTooltip } from 'reactstrap';
import { NormalButton } from '@/shared/ui';

export type ConfirmModalProps = {
  isOpen: boolean;
  onClickLeftButton?: () => void;
  onClickRightButton: () => void;
  leftButtonName?: string;
  leftButtonId?: string;
  rightButtonName: string;
  // PropTypes declares this as a required string, but the component branches
  // on isValidElement(title), so a ReactNode is genuinely accepted at runtime.
  title: React.ReactNode;
  question?: string;
  isSaving?: boolean;
  className?: string;
  hasNoButton?: boolean;
  isRedBtn?: boolean;
  customContent?: React.ReactNode;
  isPrimaryDarkTextLeftBtn?: boolean;
  isBoldLeftBtn?: boolean;
  isFilledRightBtn?: boolean;
  rightButtonId?: string;
  rightButtonWrapperId?: string;
  rightButtonTooltip?: string;
  rightButtonTooltipPlacement?: Popper.Placement;
  // used in JSX below but never declared in the original PropTypes
  disabled?: boolean;
};

//* Use classname "small-modal" for 350px width
const ConfirmModal = ({
  isOpen,
  onClickLeftButton = () => {},
  onClickRightButton,
  leftButtonName,
  leftButtonId,
  rightButtonName,
  title,
  question = '',
  isSaving = false,
  className,
  hasNoButton = false,
  isRedBtn = true,
  customContent,
  isPrimaryDarkTextLeftBtn = false,
  isBoldLeftBtn = false,
  isFilledRightBtn = true,
  rightButtonId,
  rightButtonWrapperId,
  rightButtonTooltip,
  rightButtonTooltipPlacement = 'bottom',
  disabled,
}: ConfirmModalProps) => (
  <Modal className={classnames('custom-confirm-modal', className)} isOpen={isOpen} centered>
    <div className="custom-modal-content">
      {!!title && (
        <div className="custom-modal-header mb3">{isValidElement(title) ? title : title}</div>
      )}
      {customContent || <div>{question}</div>}
    </div>
    {!hasNoButton ? (
      <div className="flex justify-between mt-3">
        {leftButtonName ? (
          <NormalButton
            id={leftButtonId}
            className={classnames(
              isPrimaryDarkTextLeftBtn ? '--primary-dark-text' : '--black-text',
              isBoldLeftBtn ? '--bold' : '',
            )}
            isText
            buttonName={leftButtonName}
            onClick={onClickLeftButton}
          />
        ) : (
          <div />
        )}
        <div id={rightButtonWrapperId || rightButtonId}>
          <NormalButton
            id={rightButtonId}
            isFilled={isFilledRightBtn}
            isSaving={isSaving}
            className={isRedBtn ? '--red' : ''}
            buttonName={rightButtonName}
            onClick={onClickRightButton}
            disabled={disabled}
          />
        </div>
        {rightButtonTooltip && (rightButtonWrapperId || rightButtonId) && (
          <UncontrolledTooltip
            className="custom-tooltip"
            target={rightButtonWrapperId || rightButtonId}
            placement={rightButtonTooltipPlacement}
            delay={{ show: 0, hide: 0 }}
            hideArrow
          >
            {rightButtonTooltip}
          </UncontrolledTooltip>
        )}
      </div>
    ) : null}
  </Modal>
);

export default ConfirmModal;
